/**
 * FloatingTabBar — mobile bottom dock with per-slot user customization.
 *
 *   • Floating glass pill, sliding gradient indicator (Framer `layoutId`).
 *   • All 5 slots are user-configurable via long-press → FavTabPicker.
 *   • Active tab: icon morphs to fill weight + scales, pulsing dot above.
 *   • 10 ms haptic on tap, 20 ms on long-press.
 *
 * Caller is responsible for:
 *   - persisting the slot array (`slotPaths`) to localStorage,
 *   - rendering the FavTabPicker and wiring its onSelect back to update slots.
 */
import { useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DeviceMobile, Layout, Storefront, Users, Trophy } from '@phosphor-icons/react';

const haptic = (ms = 10) => { try { navigator.vibrate?.(ms); } catch { /* noop */ } };

/* Fallback when a slot path cannot be resolved against the registry. */
const FALLBACK = {
  '/me':            { path: '/me',            icon: Layout,       label: 'Dashboard' },
  '/stores':        { path: '/stores',        icon: Storefront,   label: 'Stores' },
  '/reps':          { path: '/reps',          icon: Users,        label: 'Reps' },
  '/top10':         { path: '/top10',         icon: Trophy,       label: 'Top 10' },
  '/profitability': { path: '/profitability', icon: DeviceMobile, label: 'Device Pricing' },
};

const FloatingTabBar = ({
  slotPaths = [],          // length-5 array of paths, one per visible slot
  navColors = {},
  palette = {},
  paletteTools = [],       // full tool registry — resolves icon/label/color
  onLongPressSlot,         // (slotIndex: number) => void → opens FavTabPicker
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const longPressRef = useRef(null);
  const longPressFired = useRef(false);

  /* Resolve each slot path → full tab object. */
  const tabs = useMemo(() => {
    return slotPaths.map(path => {
      const found = (paletteTools || []).find(t => t.path === path);
      const fb = FALLBACK[path];
      const base = found || fb || FALLBACK['/profitability'];
      return {
        path: base.path,
        label: base.label,
        icon: base.icon,
        color: navColors[base.label] || base.color || palette.red || '#CD040B',
      };
    });
  }, [slotPaths, paletteTools, navColors, palette]);

  const activeKey = useMemo(() => {
    const match = tabs.find(t => t.path && (
      t.path === location.pathname ||
      (t.path !== '/' && location.pathname.startsWith(t.path))
    ));
    return match ? match.path : null;
  }, [tabs, location.pathname]);

  const navigateTo = (tab) => {
    if (!tab.path) return;
    if (location.pathname === tab.path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(tab.path);
  };

  /* Long-press detection. */
  const startLongPress = (idx) => () => {
    longPressFired.current = false;
    longPressRef.current = setTimeout(() => {
      longPressFired.current = true;
      haptic(20);
      onLongPressSlot?.(idx);
    }, 480);
  };
  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  return (
    <nav
      data-testid="bottom-tab-bar"
      role="tablist"
      aria-label="Primary"
      className="md:hidden ftb-shell"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="ftb-pill">
        <span aria-hidden className="ftb-glow ftb-glow-1" />
        <span aria-hidden className="ftb-glow ftb-glow-2" />

        {tabs.map((tab, idx) => {
          const Icon = tab.icon;
          const isActive = tab.path === activeKey;

          const handleClick = (e) => {
            if (longPressFired.current) {
              e.preventDefault();
              longPressFired.current = false;
              return;
            }
            haptic(10);
            navigateTo(tab);
          };

          return (
            <button
              key={`${idx}-${tab.path}`}
              type="button"
              role="tab"
              onClick={handleClick}
              onPointerDown={startLongPress(idx)}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
              onPointerCancel={cancelLongPress}
              onContextMenu={(e) => { e.preventDefault(); onLongPressSlot?.(idx); }}
              className={`ftb-btn ${isActive ? 'is-active' : ''}`}
              style={{ '--ftb-accent': tab.color }}
              data-testid={`bottom-tab-${tab.path.replace(/^\//, '')}`}>
              {isActive && (
                <motion.span
                  aria-hidden
                  layoutId="ftb-indicator"
                  className="ftb-indicator"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }} />
              )}
              <span className="ftb-icon-wrap">
                {Icon && <Icon size={20} weight={isActive ? 'fill' : 'regular'} />}
                {isActive && <span aria-hidden className="ftb-glowdot" />}
              </span>
              <span className="ftb-label">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default FloatingTabBar;
